import { myPendingOrderList } from "@/apis/crypto";
import { DialogLocal } from "@/components/Dialog";
import { OrderType, Payments } from "@/constants";
import { PAGE_SIZE } from "@/constants/config";
import { StateReducer, useCreateReducer } from "@/libs/reducer";
import { useCryptoPayments } from "@/stores/CryptoTradingStore";
import { useUserInfoStore } from "@/stores/userInfo";
import { PendingOrder } from "@/types/Api/Market/PendingOrder/myPendingOrderDetail";
import { toMoney } from "@/utils/tool";
import { useInfiniteScroll, useMount } from "ahooks";
import { AutoCenter } from "antd-mobile";
import { Action } from "antd-mobile/es/components/popover";
import { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from './Filter.module.scss'

export interface PageState {
  showSortActions: boolean;
  showFilterPopup: boolean;
  // 列表筛选项
  paymentType: Payments[];
  isSplit: OrderType[];
  tradeStatus: string[];
  professionStatus: number[];
}

const pagestate = {
  showSortActions: false,
  showFilterPopup: false,
  amount: "",
  paymentType: [],
  isSplit: [],
  tradeStatus: [],
  professionStatus:[],
};

export type ActionType =
  | "update-showSortActions"
  | "update-showFilterPopup"
  | "update-paymentType"
  | "update-isSplit"
  | "update-tradeStatus"
  | "update-professionStatus"
  | "reset_filters"
  | "update-list";

let cacheFilters: {
  amount?: string;
  paymentType?: Payments[];
  isSplit: OrderType[];
  tradeStatus: string[];
  professionStatus: number[];
} = {
  amount: "",
  paymentType: [],
  isSplit: [],
  tradeStatus: [],
  professionStatus:[],
};

const actions: StateReducer<PageState, ActionType> = (state, action) => {
  switch (action.type) {
    case "update-showFilterPopup":
      if (action.showFilterPopup) {
        cacheFilters = {
          paymentType: state.paymentType,
          isSplit: state.isSplit,
          tradeStatus: state.tradeStatus,
          professionStatus:state.professionStatus,
        };
        return Object.assign({}, state, {
          showFilterPopup: action.showFilterPopup,
        });
      } else {
        return Object.assign({}, state, {
          showFilterPopup: action.showFilterPopup,
          paymentType: cacheFilters.paymentType,
          isSplit: cacheFilters.isSplit,
          tradeStatus: cacheFilters.tradeStatus,
          professionStatus: cacheFilters.professionStatus,
        });
      }
    case "update-paymentType":
      return Object.assign({}, state, {
        paymentType: action.paymentType,
      });
    case "update-isSplit":
      return Object.assign({}, state, {
        isSplit: action.isSplit,
      });
    case "update-tradeStatus":
      return Object.assign({}, state, {
        tradeStatus: action.tradeStatus,
      });
    case "update-professionStatus":
      return Object.assign({}, state, {
        professionStatus: action.professionStatus,
      });
    case "reset_filters":
      return Object.assign({}, state, {
        paymentType: [],
        isSplit: "",
        tradeStatus: [],
        professionStatus:[]
      });
    default:
      return state;
  }
};

export const useSetup = () => {
  const navigate = useNavigate();
  const scollableRef = useRef(null);
  const [userInfo] = useUserInfoStore()((store) => [store.userInfo]);
  const cryptoPayments =  useCryptoPayments()
  const [state, dispatch] = useCreateReducer<PageState, ActionType>(
    actions,
    pagestate,
    {}
  );

  /** `const fetchList` 是一个使用 `tradeList` API 从服务器获取挂单列表的函数。它使用组件的当前状态来构造 API
  请求参数，然后调度一个操作以使用获取的订单列表更新组件状态。该函数还会在组件安装时使用“useEffect”挂钩调用一次。 */
  const infiniteList = useInfiniteScroll<{
    list: PendingOrder[];
    total: number;
  }>(
    async (d) => {
      
      const page = d ? Math.ceil(d.list.length / PAGE_SIZE) + 1 : 1;
      const response = await myPendingOrderList({
        paymentType: state.paymentType?.reduce((a, b) => a + b, "") as string || '0',
        isSplit: state.isSplit[0] ,
        page: page,
        pageSize: PAGE_SIZE,
        inProgress: state.tradeStatus.includes('isTransacting')? 1: undefined,
        status: state.tradeStatus.includes('isHanging')? 1: undefined,
        isBusiness: state.professionStatus[0],
      });

      if (!response.ok && response.code === 400) {
        DialogLocal({
          title: "提示",
          content: <AutoCenter>{response.msg}</AutoCenter>,
          confirmText: "确认",
          cancelText: "暂不绑定",
          async onConfirm() {
            navigate("/payment_add/1?m=1");
            return true;
          },
          async onCancel() {
            return true;
          },
        });
      }
      return {
        list: response.data?.list ?? [],
        total: response.data?.total ?? 0,
      };
    },
    {
      manual: true,
      target: scollableRef,
      isNoMore: (d) => d?.total === 0 || d?.total === d?.list.length,
    }
  );

  const resetFilters = useCallback(() => {
    cacheFilters = {
      paymentType: state.paymentType,
      isSplit: state.isSplit,
      tradeStatus: state.tradeStatus,
      professionStatus: state.professionStatus,
    };
    dispatch({ type: "reset_filters" });
  }, [state, dispatch]);

  const applyFilters = useCallback(() => {
    cacheFilters = {
      paymentType: state.paymentType,
      isSplit: state.isSplit,
      tradeStatus: state.tradeStatus,
      professionStatus: state.professionStatus,
    };
    dispatch({ type: "update-showFilterPopup", showFilterPopup: false });
    infiniteList.reload();
  }, [state, dispatch, infiniteList]);

  useMount(() => {
    infiniteList.reload();
  });
  const tradePayments = useMemo(() => {
    const tradePayments = [];
    for (const key in cryptoPayments.merchantPayments) {
      if (Object.prototype.hasOwnProperty.call(cryptoPayments.merchantPayments, key)) {
        const payment = cryptoPayments.merchantPayments[key];
        if(payment.list.length){
          tradePayments.push({
            // label: payment.typeName, 
            label: <span className={styles.typeBox}><span className={styles[`type${payment.type}`]}></span>{payment.typeName}</span>,
            value: payment.type,
          })
        }
        
      }
    }
    return tradePayments
  }, [ cryptoPayments.merchantPayments])
  return {
    state,
    dispatch,
    resetFilters,
    applyFilters,
    infiniteList,
    navigate,
    userInfo,
    scollableRef,
    tradePayments
  };
};
